import type {
  ButtonHTMLAttributes,
  PropsWithChildren,
  ReactNode,
  Ref,
} from 'react'
import { forwardRef } from 'react'

import { theme } from '@procurify/ui-theme'

import { Icon, type IconTypes } from '../Icon'
import { CircleLoader } from '../Loaders'
import { BaseButtonStyled } from './styles/BaseButtonStyled'
import { ButtonLoaderStyled } from './styles/ButtonLoaderStyled'
import { getAnalyticsDataAttrs } from '@ui/utils/analytics'

type NativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement>

export interface IBaseButtonProps extends NativeButtonProps {
  /**
   * Unique identifier for analytics tracking of button interactions
   */
  analyticsId?: string
  isLoading?: boolean
  icon?: IconTypes
  iconComponent?: ReactNode
  fullWidth?: boolean
  ref?: Ref<HTMLButtonElement>
  showIconOnEnd?: boolean
}

export const BaseButton = forwardRef<HTMLButtonElement, IBaseButtonProps>(
  (props: PropsWithChildren<IBaseButtonProps>, ref) => {
    const {
      children,
      analyticsId,
      isLoading,
      icon,
      iconComponent,
      disabled,
      showIconOnEnd,
      ...otherProps
    } = props

    const renderContent = () => {
      if (showIconOnEnd) {
        let iconElement = null
        if (iconComponent) {
          iconElement = iconComponent
        } else if (icon) {
          iconElement = <Icon icon={icon} className={'end-icon'} />
        }

        return (
          <>
            {children}
            {iconElement}
          </>
        )
      }

      return (
        <>
          {iconComponent ? iconComponent : icon ? <Icon icon={icon} /> : null}
          {children}
        </>
      )
    }

    const analyticsProps = getAnalyticsDataAttrs(analyticsId)

    return (
      <BaseButtonStyled
        ref={ref}
        disabled={isLoading || disabled}
        {...analyticsProps}
        {...otherProps}
      >
        {renderContent()}

        {isLoading && (
          <ButtonLoaderStyled>
            <CircleLoader color={theme.colors.text.primary} size={14} />
          </ButtonLoaderStyled>
        )}
      </BaseButtonStyled>
    )
  }
)

BaseButton.displayName = 'BaseButton'
